<template>
    <div>
        <pc-button
            id="NewIncentiveDesignButton"
            primary
            v-on:click="$emit('new')">
            {{ newIncentiveDesignButtonText }}
        </pc-button>
        <pc-table
            id="IncentiveDesignTable"
            show-items-per-page-selector
            v-bind:items="designs"
            v-bind:columns="columns"
            v-bind:items-per-page="13"
            v-bind:empty-text="emptyIncentiveDesignTableText"
            key-prop="PublicId"
            save-state
            state-key="incentive-design-list">
            <template v-slot:IncentiveTypeId="design">
                {{ getIncentiveTypeText(design) }}
            </template>
            <template v-slot:CapByApprovedAmount="design">
                {{ getApprovedAmountCapText(design) }}
            </template>
            <template v-slot:Metadata0="design">
                {{ getIncentiveMetadataValue(design, 0) }}
            </template>
            <template v-slot:Metadata1="design">
                {{ getIncentiveMetadataValue(design, 1) }}
            </template>
            <template v-slot:Metadata2="design">
                {{ getIncentiveMetadataValue(design, 2) }}
            </template>
            <template v-slot:IsEnabled="design">
                {{ getEnabledStatusText(design) }}
            </template>
            <template v-slot:IsEditable="design">
                {{ getEditableStatusText(design) }}
            </template>
            <template v-slot:actionsRow="row">
                <div class="d-flex mt-1">
                    <pc-button
                        id="ViewIncentiveDesignButton"
                        class="me-1"
                        data-test-role="view-incentive-design"
                        v-on:click="$emit('view', row.PublicId)">
                        {{ viewButtonText }}
                    </pc-button>
                    <pc-button
                        v-if="showEditButton(row)"
                        id="EditIncentiveDesignButton"
                        class="me-1"
                        data-test-role="edit-incentive-design"
                        v-on:click="$emit('edit', row.PublicId)">
                        {{ editButtonText }}
                    </pc-button>
                    <pc-button
                        v-if="showCpAdminEditButton(row)"
                        id="CPAdminEditIncentiveDesignButton"
                        warning
                        class="me-1"
                        data-test-role="cpadmin-edit-incentive-design"
                        v-on:click="$emit('cpadminedit', row.PublicId)">
                        {{ editButtonText }}
                    </pc-button>
                    <pc-button
                        id="CopyIncentiveDesignButton"
                        class="me-1"
                        data-test-role="copy-incentive-design"
                        v-on:click="$emit('copy', row.PublicId)">
                        {{ copyButtonText }}
                    </pc-button>
                    <pc-button
                        v-if="showSetExpirationDateButton(row)"
                        id="SetExpirationDateButton"
                        class="me-1"
                        data-test-role="set-expdate-incentive-design"
                        v-on:click="$emit('setexpirationdate', row.PublicId)">
                        {{ setExpDateButtonText }}
                    </pc-button>
                    <pc-button
                        v-if="!row.IsEnabled"
                        id="EnableIncentiveDesignButton"
                        class="me-1"
                        data-test-role="enable-incentive-design"
                        v-on:click="$emit('enable', row.PublicId)">
                        {{ enableButtonText }}
                    </pc-button>
                    <pc-button
                        v-if="row.IsEnabled"
                        id="DisableIncentiveDesignButton"
                        class="me-1"
                        data-test-role="disable-incentive-design"
                        v-on:click="$emit('disable', row.PublicId)">
                        {{ disableButtonText }}
                    </pc-button>
                    <pc-button
                        v-if="row.IsEditable"
                        id="DeleteIncentiveDesignButton"
                        link
                        class="ms-auto"
                        data-test-role="delete-incentive-design"
                        v-on:click="$emit('delete', row.PublicId)">
                        {{ deleteButtonText }}
                    </pc-button>
                </div>
            </template>
        </pc-table>
    </div>
</template>

<script>
    import { NULL_TIMESTAMP_DISPLAY } from "./incentiveDesignList.vue";
    import { INCENTIVE_TYPE } from "./editIncentiveDesign.vue";
    import { getClientTimestampTableColumn } from "Util/clientDateTime";
    import { ClientDate, dateStrToClientDate } from "Util/clientDate";
    import { KEYS, NAMESPACE } from "./i18n/incentiveDesignsTab.i18n";
    import { mapI18n } from "Util/i18n";

    const EDIT_STATUS = {
        ViewOnly: 0,
        Edit: 1,
        CpAdminEdit: 2
    };

    export default {
        name: "IncentiveDesignsTab",
        props: {
            designs: { type: Array, required: true },
            approvalStatusId: { type: String, required: true },
            metadataColumns: { type: Array, required: true },
            hasCpadminWritePrivilege: { type: Boolean, required: true }
        },
        emits: ["new", "view", "edit", "cpadminedit", "copy", "setexpirationdate", "enable", "disable", "delete"],
        data() {
            return {
                todayClientDate: new ClientDate(this.$app)
            };
        },
        computed: {
            columns() {
                const cols = [
                    {
                        header: this.nameColumnHeader,
                        property: "Name",
                        orderable: true,
                        searchable: true,
                        visible: true
                    },
                    {
                        header: this.typeColumnHeader,
                        property: "IncentiveTypeId",
                        orderable: true,
                        searchable: true,
                        visible: true,
                        searchValue: function (design) {
                            return this.getIncentiveTypeText(design);
                        }.bind(this),
                        sortValue: function (design) {
                            return this.getIncentiveTypeText(design);
                        }.bind(this)
                    },
                    getClientTimestampTableColumn(
                        this.startDateColumnHeader,
                        "StartDateTimestamp",
                        NULL_TIMESTAMP_DISPLAY,
                        true,
                        false
                    ),
                    getClientTimestampTableColumn(
                        this.expirationDateColumnHeader,
                        "ExpirationDateTimestamp",
                        NULL_TIMESTAMP_DISPLAY,
                        true,
                        true
                    ),
                    {
                        header: this.conditionsColumnHeader,
                        property: "Conditions",
                        orderable: true,
                        searchable: true,
                        visible: true
                    },
                    {
                        header: this.approvedAmountCapHeader,
                        property: "CapByApprovedAmount",
                        orderable: true,
                        searchable: false,
                        visible: this.approvalStatusId
                    },
                    // Metadata columns (if any) are inserted here
                    {
                        header: this.statusColumnHeader,
                        property: "IsEnabled",
                        orderable: true,
                        searchable: true,
                        visible: true,
                        searchValue: function (design) {
                            return this.getEnabledStatusText(design);
                        }.bind(this),
                        sortValue: function (design) {
                            return this.getEnabledStatusText(design);
                        }.bind(this)
                    },
                    {
                        header: this.editableColumnHeader,
                        property: "IsEditable",
                        orderable: true,
                        searchable: true,
                        visible: true,
                        searchValue: function (design) {
                            return this.getEditableStatusText(design);
                        }.bind(this),
                        sortValue: function (design) {
                            return this.getEditableStatusText(design);
                        }.bind(this)
                    },
                    {
                        property: "PublicId",
                        orderable: false,
                        searchable: false,
                        visible: false
                    }
                ];

                if (this.metadataColumns.length > 0) {
                    const i = cols.findIndex((c) => c.property === "IsEnabled");
                    cols.splice(i, 0, ...this.metadataTableColumns);
                }

                return cols;
            },
            metadataTableColumns() {
                const mcols = [];
                for (let i = 0; i < this.metadataColumns.length; i++) {
                    mcols.push({
                        header: this.metadataColumns[i].SchemaRowName,
                        property: "Metadata" + i.toString(),
                        orderable: true,
                        searchable: true,
                        visible: true
                    });
                }
                return mcols;
            },
            //Support for localization
            ...mapI18n(
                {
                    ...KEYS
                },
                NAMESPACE
            )
        },
        methods: {
            getIncentiveTypeText(design) {
                switch (design.IncentiveTypeId) {
                    case INCENTIVE_TYPE.OneTime:
                        return this.incentiveTypeOneTimeText;
                    case INCENTIVE_TYPE.PBI:
                        return this.incentiveTypePBIText;
                    case INCENTIVE_TYPE.Flat:
                        if (design.IsFormulaIncentive) {
                            return this.incentiveTypeFormulaText;
                        } else {
                            return this.incentiveTypeFlatText;
                        }
                    case INCENTIVE_TYPE.CGBOneRatePBI:
                        return this.incentiveTypeCGBOneRatePBIText;
                    case INCENTIVE_TYPE.CGBTieredPBI:
                        return this.incentiveTypeCGBTieredPBIText;
                    case INCENTIVE_TYPE.CGBTieredEPBB:
                        return this.incentiveTypeCGBTieredEPBBText;
                }
                return "";
            },
            getApprovedAmountCapText(design) {
                return design.CapByApprovedAmount ? this.yesText : this.noText;
            },
            getIncentiveMetadataValue(design, metadataIndex) {
                let metadataValue = "";
                const metadataColumnId = this.metadataColumns[metadataIndex].PublicId;
                if (design.IncentiveMetadataList) {
                    const designMetadata = design.IncentiveMetadataList.find(
                        (m) => m.MetadataFieldPublicId === metadataColumnId
                    );
                    if (designMetadata) {
                        metadataValue = designMetadata.MetadataValue;
                    }
                }
                return metadataValue;
            },
            getEnabledStatusText(design) {
                if (!design.IsEnabled) {
                    return this.disabledStatusText;
                }
                // The design is Enabled; check its active date range (if any)
                if (this.isFuture(design)) {
                    return this.futureStatusText;
                }
                if (this.isExpired(design)) {
                    return this.expiredStatusText;
                }
                return this.enabledStatusText;
            },
            isFuture(design) {
                // If there is no StartDateTimestamp, Ticks = 0
                if (design.StartDateTimestamp.Ticks > 0) {
                    const startClientDate = dateStrToClientDate(this.$app, design.StartDateTimestamp.DateDisplay);
                    return this.todayClientDate.isBefore(startClientDate, true);
                }
                return false;
            },
            isExpired(design) {
                // If there is no ExpirationDateTimestamp, Ticks = 0
                if (design.ExpirationDateTimestamp.Ticks > 0) {
                    const expirationClientDate = dateStrToClientDate(
                        this.$app,
                        design.ExpirationDateTimestamp.DateDisplay
                    );
                    return expirationClientDate.isBefore(this.todayClientDate, false);
                }
                return false;
            },
            getEditableStatusValue(design) {
                if (design.IsEditable) {
                    return EDIT_STATUS.Edit;
                } else if (this.hasCpadminWritePrivilege) {
                    return EDIT_STATUS.CpAdminEdit;
                } else {
                    return EDIT_STATUS.ViewOnly;
                }
            },
            getEditableStatusText(design) {
                const editableValue = this.getEditableStatusValue(design);
                let editableText = "";
                switch (editableValue) {
                    case EDIT_STATUS.Edit:
                        editableText = this.yesText;
                        break;
                    case EDIT_STATUS.ViewOnly:
                        editableText = this.noText;
                        break;
                    case EDIT_STATUS.CpAdminEdit:
                        if (this.hasCpadminWritePrivilege) {
                            editableText = this.cpadminText;
                        } else {
                            editableText = this.noText;
                        }
                        break;
                }
                return editableText;
            },
            showEditButton(design) {
                return this.getEditableStatusValue(design) === EDIT_STATUS.Edit;
            },
            showCpAdminEditButton(design) {
                return this.getEditableStatusValue(design) === EDIT_STATUS.CpAdminEdit;
            },
            showSetExpirationDateButton(design) {
                return !design.IsEditable && !this.isExpired(design);
            }
        }
    };
</script>
